import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";

export const PayrollSection = () => {
  const [employees, setEmployees] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [generating, setGenerating] = useState(false);

  const fetchPayroll = async () => {
    const { data } = await axios.get(`${API}/payroll`, { params: { month }, withCredentials: true });
    setPayrolls(data);
  };

  useEffect(() => {
    axios.get(`${API}/employees`, { withCredentials: true }).then((r) => setEmployees(r.data));
  }, []);

  useEffect(() => {
    fetchPayroll();
  }, [month]);

  const generate = async () => {
    if (!employeeId) {
      toast.error("Select an employee");
      return;
    }
    setGenerating(true);
    try {
      await axios.post(`${API}/payroll/generate`, { employee_id: employeeId, month }, { withCredentials: true });
      toast.success("Payroll generated");
      fetchPayroll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate payroll");
    } finally {
      setGenerating(false);
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/payroll/${id}`, { status }, { withCredentials: true });
    toast.success("Status updated");
    fetchPayroll();
  };

  return (
    <div data-testid="payroll-section">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Employee</span>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="w-52" data-testid="payroll-employee-select">
              <SelectValue placeholder="Select employee" />
            </SelectTrigger>
            <SelectContent>
              {employees.filter((e) => e.status === "active").map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Month</span>
          <Input type="month" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payroll-month-input" />
        </div>
        <Button onClick={generate} disabled={generating} className="gap-2" data-testid="generate-payroll-button">
          <Calculator className="h-4 w-4" /> Generate Payroll
        </Button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table data-testid="payroll-table">
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Month</TableHead>
              <TableHead>Days</TableHead>
              <TableHead>OT Hrs</TableHead>
              <TableHead>Gross</TableHead>
              <TableHead>Deductions</TableHead>
              <TableHead>Net Pay</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Payslip</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payrolls.map((p) => (
              <TableRow key={p.id} data-testid={`payroll-row-${p.id}`}>
                <TableCell className="font-medium">{p.employee_name}</TableCell>
                <TableCell>{p.month}</TableCell>
                <TableCell>{p.days_present}</TableCell>
                <TableCell>{p.overtime_hours}</TableCell>
                <TableCell>₹{p.gross}</TableCell>
                <TableCell>₹{p.total_deductions}</TableCell>
                <TableCell className="font-semibold">₹{p.net_pay}</TableCell>
                <TableCell>
                  <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}>
                    <SelectTrigger className="w-32" data-testid={`payroll-status-${p.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="generated">generated</SelectItem>
                      <SelectItem value="paid">paid</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    data-testid={`payslip-download-${p.id}`}
                    onClick={() => window.open(`${API}/payroll/${p.id}/payslip`, "_blank")}
                  >
                    <FileDown className="h-3.5 w-3.5" /> PDF
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {payrolls.length === 0 && (
          <p className="p-8 text-center text-sm text-muted-foreground">No payroll records for {month}.</p>
        )}
      </div>
    </div>
  );
};
